import useTranslation from 'next-translate/useTranslation';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';

import type { EntryShareholder } from '../../utils/resolveEntryOwnership';
import { formatSharePercent } from '../../utils/formatSharePercent';
import GamecenterStandingsAvatar from '../GamecenterStandingsAvatar/GamecenterStandingsAvatar';
import PickControlIndicator from '../PickControlIndicator/PickControlIndicator';

import { AVATAR_SIZE, useStyles } from './EntryShareholderRow.styles';

const resolveShareholderHref = (
  shareholder: EntryShareholder,
  contestId: string,
  entryId: string
) =>
  shareholder.isCurrentUser
    ? Routes.gamecenterPicks(contestId, { entryId })
    : Routes.gamecenterStandingsOverall(contestId, {
        entryId,
        entryUsername: shareholder.user.handle,
      });

function EntryShareholderRow({
  shareholder,
  contestId,
  entryId,
  showOriginalOwner,
  showPickController,
  onNavigate,
}: {
  shareholder: EntryShareholder;
  contestId: string;
  entryId: string;
  showOriginalOwner: boolean;
  showPickController: boolean;
  onNavigate: () => void;
}) {
  const { classes } = useStyles();
  const { t } = useTranslation('contest');

  const isOriginalOwner = showOriginalOwner && shareholder.acquiredVia === 'original';
  const makesPicks = showPickController && shareholder.isPickController;

  return (
    <div className={classes.row}>
      <GamecenterStandingsAvatar
        contestId={contestId}
        entrant={{
          id: shareholder.user.id,
          handle: shareholder.user.handle,
          avatarUrl: shareholder.user.avatarUrl ?? undefined,
        }}
        size={AVATAR_SIZE}
      />
      <div className={classes.identity}>
        <UnstyledNextLink
          href={resolveShareholderHref(shareholder, contestId, entryId)}
          onClick={onNavigate}
          className={classes.shareholderLink}
        >
          <Text variant="subhead-small" truncate>
            {shareholder.isCurrentUser
              ? t('gamecenter.common.entryOwnership.you')
              : shareholder.user.handle}
          </Text>
        </UnstyledNextLink>
        {(isOriginalOwner || makesPicks) && (
          <div className={classes.meta}>
            {isOriginalOwner && (
              <Text variant="body-small">
                {t('gamecenter.common.entryOwnership.originalOwner')}
              </Text>
            )}
            {isOriginalOwner && makesPicks && (
              <Text variant="body-small" className={classes.separator}>
                •
              </Text>
            )}
            {makesPicks && (
              <span className={classes.makesPicks}>
                <PickControlIndicator isPickController />
                <Text variant="subhead-small">
                  {t('gamecenter.common.entryOwnership.makesPicks')}
                </Text>
              </span>
            )}
          </div>
        )}
      </div>
      <Text variant="subhead-small">{formatSharePercent(shareholder.shareBps)}</Text>
    </div>
  );
}

export default EntryShareholderRow;
